// Code-action: "Add missing table to FROM" — Slice 13.
//
// Triggered when the cursor sits on an unresolved column reference whose
// referenced table is NOT in scope. Surfaces a quickfix that adds the table
// to the FROM clause with an FK-driven JOIN (or a CROSS JOIN fallback when no
// FK edge connects the table to anything already in scope).
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 13.
//
// Two trigger shapes:
//   1. Qualified `prefix.column`  — the user typed an explicit table prefix
//      that doesn't match any in-scope alias / unaliased relation, AND
//        a) a real schema table named `prefix` exists and owns `column`
//           (the user typed the table name out, just forgot to FROM it), OR
//        b) no real table named `prefix` exists, but tables in the schema
//           own `column` and the user's `prefix` was meant as an alias —
//           we fan out one candidate per such table, keeping `prefix` as
//           the alias (so `o.total` keeps working after the fix).
//   2. Bare `column` — single-part identifier whose name doesn't resolve
//      against any in-scope relation. We surface tables in the schema that
//      own `column` and aren't already in scope. To keep the menu signal-
//      to-noise high we only surface candidates with at least one FK edge
//      connecting them to an already-in-scope relation.
//
// For each candidate the fix walks the OE tree's `keys-folder` (Slice 12's
// pattern) for FK edges to / from in-scope relations. Each FK edge produces
// ONE quickfix (`add INNER JOIN <table> ON <pred>`); when no FK edge exists
// a candidate yields TWO fallback fixes — `CROSS JOIN <table>` (warns in
// the title) and `INNER JOIN <table> ON /* TODO */` (manual ON placeholder).
//
// Structure: this file is the barrel + thin orchestrator. The responsibility
// units live in sibling addMissingTableToFrom.* modules (types / parse /
// scope / tree / fk / candidates) — see the imports below. The public import
// surface (`addMissingTableToFromEntry`, `collectAddMissingTableToFromEditsForTest`)
// stays on this exact path so no consumer changes.

import type { ParseOptions } from '../../../sql-grammar/model';
import { buildLineIndex, rangeFromOffsets } from '../../../sql-grammar/document/lineIndex';
import { findDottedChainStart, parseDottedChain } from '../../../sql-resolver/dotted/dottedParts';
import { extractQueryStructure } from '../../../sql-resolver/structure/extractQueryStructure';
import type { QueryStructure } from '../../../sql-resolver/types/queryStructure';
import type { DatabaseExplorerNode } from '../../../explorer/models/DatabaseTreeModels';
import { databaseEngineIdToSqlEngine } from '../../../workspace/visual-query-editor/edit/livePullSchemaSnapshot';
import { defaultSchemaForEngine } from '../../go-to/externalNavigation';
import type {
  CodeActionContext,
  CodeActionEdit,
  CodeActionRegistryEntry,
} from '../../types';
import {
  defaultFoldingForEngine,
  extendCursorThroughChain,
  parseBuffer,
  readLiveTree,
} from './addMissingTableToFrom.parse';
import { collectInScopeRelations } from './addMissingTableToFrom.scope';
import { collectCandidates } from './addMissingTableToFrom.candidates';
import { collectFkEdges } from './addMissingTableToFrom.fk';

const ENTRY_ID = 'add-missing-table-to-from' as const;

export const addMissingTableToFromEntry: CodeActionRegistryEntry = {
  id: ENTRY_ID,
  kind: 'quickfix',
  title: 'Add missing table to FROM',
  canApply: (ctx) => collectFixes(ctx, readLiveTree).length > 0,
  buildEdits: (ctx) => collectFixes(ctx, readLiveTree),
};

/** Test seam — lets unit tests pass a hand-built OE tree without touching
 *  the live Zustand store. Mirrors `collectAddMissingJoinPredicateEditsForTest`
 *  from Slice 12. */
export function collectAddMissingTableToFromEditsForTest(
  ctx: CodeActionContext,
  getTree: () => readonly DatabaseExplorerNode[],
): readonly CodeActionEdit[] {
  return collectFixes(ctx, getTree);
}

function collectFixes(
  ctx: CodeActionContext,
  getTree: () => readonly DatabaseExplorerNode[],
): readonly CodeActionEdit[] {
  const source = ctx.sourceText;
  if (!source || source.trim().length === 0) return [];
  const connectionId = ctx.connectionId;
  if (!connectionId) return [];

  const sqlEngine = databaseEngineIdToSqlEngine(ctx.engineId) ?? 'mssql';
  const options: ParseOptions = {
    engine: sqlEngine,
    identifierFolding: defaultFoldingForEngine(sqlEngine),
  };

  // 1. Locate the chain at the cursor (1-part bare or 2-part qualified).
  //    The user may cursor anywhere on the chain — extend through trailing
  //    `.<ident>` segments so cursor on `orders` in `orders.total` still
  //    yields a 2-part chain.
  const cursorAtEnd = extendCursorThroughChain(source, ctx.cursor.offset);
  const chainStart = findDottedChainStart(source, cursorAtEnd, options);
  const chain = parseDottedChain(source, chainStart, cursorAtEnd, options);
  if (!chain || chain.parts.length === 0 || chain.parts.length > 2) return [];

  // 2. Parse + extract structure.
  const buffer = parseBuffer(source, options);
  if (!buffer) return [];
  const stmt = buffer.documentStructure.statementAt(ctx.cursor.offset)
    ?? buffer.documentStructure.statements[0]
    ?? null;
  if (!stmt) return [];

  let structure: QueryStructure;
  try {
    structure = extractQueryStructure(buffer, stmt);
  } catch {
    return [];
  }
  if (structure.kind !== 'select') return [];

  const inScope = collectInScopeRelations(structure, ctx.engineId);
  if (inScope.length === 0) return [];

  // 3. Compute insertion offset = end of the last FROM/JOIN fragment.
  const insertOffset = computeInsertOffset(structure);
  if (insertOffset === null) return [];

  const treeData = getTree();
  const defaultSchema = defaultSchemaForEngine(ctx.engineId).toLowerCase();

  // 4. Build candidate list per the two trigger shapes.
  const candidates = collectCandidates(
    chain.parts,
    treeData,
    ctx.engineId,
    connectionId,
    ctx.database ?? null,
    inScope,
    defaultSchema,
  );
  if (candidates.length === 0) return [];

  // 5. For each candidate, emit either FK-driven fixes or CROSS-JOIN +
  //    manual-ON fallback fixes.
  const lineIdx = buildLineIndex(source);
  const edits: CodeActionEdit[] = [];

  for (const cand of candidates) {
    const candTableText = cand.aliasText
      ? `${cand.tableName} ${cand.aliasText}`
      : cand.tableName;
    const candDisplayRef = cand.aliasText ?? cand.tableName;

    const fkOptions = collectFkEdges(
      treeData,
      ctx.engineId,
      connectionId,
      ctx.database ?? null,
      cand,
      inScope,
      defaultSchema,
    );

    if (fkOptions.length > 0) {
      for (const fk of fkOptions) {
        const conds = fk.pairs.map((p) =>
          fk.fkSide === 'candidate'
            ? `${candDisplayRef}.${p.localCol} = ${fk.other.displayRef}.${p.remoteCol}`
            : `${fk.other.displayRef}.${p.localCol} = ${candDisplayRef}.${p.remoteCol}`,
        );
        const onText = conds.join(' AND ');
        edits.push({
          id: ENTRY_ID,
          description: `Add INNER JOIN ${candDisplayRef} ON ${onText}`,
          textEdits: [
            {
              range: rangeFromOffsets(lineIdx, insertOffset, insertOffset),
              newText: ` INNER JOIN ${candTableText} ON ${onText}`,
            },
          ],
        });
      }
    } else {
      // No FK edge — emit CROSS JOIN + manual ON placeholder.
      edits.push({
        id: ENTRY_ID,
        description: `Add CROSS JOIN ${candDisplayRef} (no FK found)`,
        textEdits: [
          {
            range: rangeFromOffsets(lineIdx, insertOffset, insertOffset),
            newText: ` CROSS JOIN ${candTableText}`,
          },
        ],
      });
      edits.push({
        id: ENTRY_ID,
        description: `Add INNER JOIN ${candDisplayRef} ON ... (manual)`,
        textEdits: [
          {
            range: rangeFromOffsets(lineIdx, insertOffset, insertOffset),
            newText: ` INNER JOIN ${candTableText} ON /* TODO: predicate */`,
          },
        ],
      });
    }
  }

  edits.sort((a, b) => (a.description < b.description ? -1 : a.description > b.description ? 1 : 0));
  return edits;
}

// ─── Insertion offset ──────────────────────────────────────────────────────

function computeInsertOffset(structure: QueryStructure): number | null {
  let max: number | null = null;
  for (const fi of structure.fromItems ?? []) {
    if (max === null || fi.range.endOffset > max) max = fi.range.endOffset;
  }
  for (const j of structure.joins ?? []) {
    if (max === null || j.range.endOffset > max) max = j.range.endOffset;
  }
  return max;
}
